import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Modal from "react-bootstrap/Modal";
import currencyFlags from "../../Components/Data/CurrencyFlags.json"; // Import currency flags JSON

const initialFieldValues = {
  vendorId: "00000000-0000-0000-0000-000000000000",
  userId: "00000000-0000-0000-0000-000000000000",
  vendorName: "",
  panNumber: "",
  gstNo: "",
  phoneNumber: "",
  email: "",
  currencyCode: "",
  address: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
};

const VendorFormModal = ({
  show,
  onHide,
  recordForEdit,
  onSuccess,
  mode = "add", // 'add' or 'edit'
}) => {
  const { user } = useAuth();
  const [btnLoading, setBtnLoading] = useState(false);
  const [values, setValues] = useState(initialFieldValues);
  const [errors, setErrors] = useState({});
  const [countries, setCountries] = useState([]);

  // Process countries data from currencyFlags.json
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);
    }
  }, []);

  // Get user's default currency and country from auth context
  const getUserDefaultCurrency = () => {
    return user?.currency || "";
  };

  const getUserDefaultCountry = () => {
    return user?.country || "IN"; // Default to India if not available
  };

  // Initialize form with user defaults when opening in add mode
  const getInitialValues = () => {
    return {
      ...initialFieldValues,
      currencyCode: getUserDefaultCurrency(),
      country: getUserDefaultCountry(),
    };
  };

  useEffect(() => {
    if (recordForEdit) {
      // For edit mode, use the record data
      setValues(recordForEdit);
    } else {
      // For add mode, use initial values with user defaults
      setValues(getInitialValues());
    }
  }, [recordForEdit, show]);

  const validate = () => {
    let temp = {};
    temp.vendorName = values.vendorName === "" ? false : true;
    temp.phoneNumber = values.phoneNumber === "" ? false : true;
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setBtnLoading(true);
      var formData = {
        vendorId: values.vendorId,
        userId: user?.id,
        vendorName: values.vendorName,
        panNumber: values.panNumber,
        gstNo: values.gstNo,
        phoneNumber: values.phoneNumber,
        email: values.email,
        currencyCode: values.currencyCode || getUserDefaultCurrency(),
        address: values.address,
        city: values.city,
        state: values.state,
        country: values.country || getUserDefaultCountry(),
        postalCode: values.postalCode,
      };
      try {
        const isNew =
          formData.vendorId === "00000000-0000-0000-0000-000000000000";

        if (isNew) {
          await apiService.create(config.CREATEVENDOR, formData);
        } else {
          await apiService.update(config.UPDATEVENDOR, formData);
        }

        resetForm();
        if (onSuccess) {
          onSuccess();
        }
      } finally {
        setBtnLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const fieldsToUppercase = [
      "vendorName",
      "panNumber",
      "gstNo",
      "currencyCode",
    ];

    // When country changes, update both country and currency
    if (name === "country") {
      const selectedCountry = countries.find(
        (country) => country.countryCode === value
      );
      setValues({
        ...values,
        country: value,
        currencyCode: selectedCountry?.code || getUserDefaultCurrency(),
      });
    } else {
      setValues({
        ...values,
        [name]: fieldsToUppercase.includes(name) ? value.toUpperCase() : value,
      });
    }
  };

  const resetForm = () => {
    setValues(getInitialValues());
    setErrors({});
    onHide();
  };

  const applyErrorClass = (field) =>
    field in errors && errors[field] === false ? " form-control-danger" : "";

  const getModalTitle = () => {
    if (mode === "edit") return "Edit Vendor";
    return values.vendorId === "00000000-0000-0000-0000-000000000000"
      ? "Add Vendor"
      : "Edit Vendor";
  };

  // Check if current value is the user's default
  const isDefaultCountry = (countryCode) => {
    return countryCode === getUserDefaultCountry();
  };

  return (
    <Modal show={show} onHide={resetForm} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          {/* First row with 3 fields */}
          <div className="row">
            <div className="col-lg-4 mb-3">
              <label htmlFor="vendorName" className="form-label">
                Vendor Name *
              </label>
              <input
                type="text"
                value={values.vendorName}
                name="vendorName"
                onChange={handleInputChange}
                className={"form-control" + applyErrorClass("vendorName")}
                placeholder="Please Enter Vendor Name"
                maxLength={45}
              />
              {errors.vendorName === false && (
                <div className="validationerror">Please enter name</div>
              )}
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="phoneNumber" className="form-label">
                Phone Number *
              </label>
              <input
                type="text"
                value={values.phoneNumber}
                name="phoneNumber"
                onChange={handleInputChange}
                className={"form-control" + applyErrorClass("phoneNumber")}
                placeholder="Please Enter Phone Number"
                maxLength={15}
              />
              {errors.phoneNumber === false && (
                <div className="validationerror">Please enter phone number</div>
              )}
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                value={values.email}
                name="email"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter Email"
                maxLength={45}
              />
            </div>
          </div>

          {/* Second row with 3 fields */}
          <div className="row">
            <div className="col-lg-8 mb-3">
              <label htmlFor="address" className="form-label">
                Address
              </label>
              <input
                type="text"
                value={values.address}
                name="address"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter Address"
                maxLength={250}
              />
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="city" className="form-label">
                City
              </label>
              <input
                type="text"
                value={values.city}
                name="city"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter City"
                maxLength={50}
              />
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="state" className="form-label">
                State
              </label>
              <input
                type="text"
                value={values.state}
                name="state"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter State"
                maxLength={50}
              />
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="postalCode" className="form-label">
                Postal Code
              </label>
              <input
                type="text"
                value={values.postalCode}
                name="postalCode"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter Postal Code"
                maxLength={10}
              />
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="country" className="form-label">
                Country
              </label>
              <select
                name="country"
                value={values.country}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="">Select Country</option>
                {countries.map((country) => (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.flag && (
                      <img
                        src={country.flag}
                        alt={country.country}
                        style={{
                          width: "20px",
                          height: "15px",
                          marginRight: "8px",
                          verticalAlign: "middle",
                        }}
                      />
                    )}
                    {country.country} - {country.countryCode}
                    {isDefaultCountry(country.countryCode) && " ★"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Third row with 3 fields */}
          <div className="row">
            <div className="col-lg-4 mb-3">
              <label htmlFor="currencyCode" className="form-label">
                Currency
              </label>
              <input
                type="text"
                value={values.currencyCode}
                name="currencyCode"
                className="form-control"
                placeholder="Currency will auto-fill based on country"
                readOnly
              />
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="gstNo" className="form-label">
                GST No
              </label>
              <input
                type="text"
                value={values.gstNo}
                name="gstNo"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter GST No"
                maxLength={25}
              />
            </div>
            <div className="col-lg-4 mb-3">
              <label htmlFor="panNumber" className="form-label">
                PAN Number
              </label>
              <input
                type="text"
                value={values.panNumber}
                name="panNumber"
                onChange={handleInputChange}
                className="form-control"
                placeholder="Please Enter PAN Number"
                maxLength={10}
              />
            </div>
          </div>

          <Modal.Footer>
            <div className="hstack gap-2 justify-content-end">
              {btnLoading ? (
                <button type="button" className="btn btn-primary" disabled>
                  Please wait...
                </button>
              ) : (
                <button type="submit" className="btn btn-primary">
                  {values.vendorId === "00000000-0000-0000-0000-000000000000"
                    ? "Submit"
                    : "Update"}
                </button>
              )}
              <button
                type="button"
                className="btn btn-danger"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </Modal.Footer>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default VendorFormModal;
